import '@aytm/app/swidget/base/questions/Ecommerce/Ecommerce/Store/ModelViewer.ts'
import React, { useMemo, useRef } from 'react'
import style from './AddedToCartModal.module.scss'
import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types'
import useOnClickOutside from '@aytm/lib/hooks/useOnClickOutside'
import { getSmallAssetPath } from '@aytm/lib/common/assets'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale'
import MultipleCarousel from './MultipleCarousel'
import ProductPreview from './ProductPreview'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

interface IAddedToCartModalProps {
  answer: IEcommerceAnswerSchema
  schema: IEcommerceQuestionSchema
  index: number
  amount: number
  quantity: number
  findability: boolean
  onItemClose: () => void
  onCartOpen: () => void
  onItemSelect: (index: number) => void
  onAddToCart: (qnt: number, index: number) => void
  onModalClose: () => void
}

const AddedToCartModal = ({
  answer,
  schema,
  index,
  amount,
  quantity,
  findability,
  onItemClose,
  onCartOpen,
  onItemSelect,
  onAddToCart,
  onModalClose
}: IAddedToCartModalProps) => {
  const { t, languageTag } = useSurveyLocale()
  const viewport = useViewport()
  const featured = useMemo(() => schema.answers.filter(answer => answer.featured), [schema])
  const modalBodyRef = useRef<HTMLDivElement>(null)
  useOnClickOutside(modalBodyRef, onModalClose)

  return (
    <div
      className={`${style.addedToCartModalLayout} ${viewport.mobile ? style.addedToCartModalLayoutMobile : ''}`}
      data-testid="SW-EC-AddedToCartModal"
    >
      <div className={style.addedToCartModal} ref={modalBodyRef}>
        <div className={style.modalHeader}>
          <div className={style.headerText}>{t('questions', 'ec', 'costco', 'added')}</div>
          <button className={style.close} onClick={onModalClose}>
            <i className="pi pi-times" />
          </button>
        </div>
        <div className={style.modalContent}>
          <div className={style.product}>
            {answer.image ? (
              <div
                className={style.image}
                role="img"
                aria-label={answer.image?.alt_text || t('questions', 'img', 'noAlt')}
                style={{ backgroundImage: `url('${getSmallAssetPath(answer.image)}')` }}
              />
            ) : (
              <div className={style.noImage} />
            )}
            <div className={style.info}>
              <div className={style.caption} dangerouslySetInnerHTML={{ __html: answer.text }} />
              <div
                className={style.code}
                dangerouslySetInnerHTML={{
                  __html: t<(code: string) => string>('questions', 'ec', 'costco', 'code')(`187893${index}`)
                }}
              />
              <div className={style.amount}>
                {t('questions', 'ec', 'amazon', 'qty')}:&nbsp;{amount}
              </div>
              <div
                className={style.price}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(
                    answer.discounted_price ? answer.discounted_price : answer.price,
                    schema.price_mask,
                    languageTag,
                    true,
                    false
                  )}`
                }}
              />
            </div>
          </div>
          <div className={style.approved}>
            <i className="pi pi-check" />
            <span dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'costco', 'forDelivery') }} />
          </div>
          <div className={style.buttons}>
            <button className={style.continue} onClick={onModalClose}>
              {t('questions', 'ec', 'costco', 'continueShopping')}
            </button>
            <button className={style.viewCart} onClick={onCartOpen}>
              {t('questions', 'ec', 'costco', 'viewCart')}
            </button>
          </div>
          {featured.length > 0 && (
            <div className={style.alsoBought}>
              <div className={style.title}>{t('questions', 'ec', 'costco', 'alsoBought')}</div>
              <MultipleCarousel
                slidesPerView={viewport.mobile ? 2 : 3}
                showNavigation={viewport.mobile ? false : featured.length > 3}
              >
                {schema.answers.map((answer, index) => (
                  <>
                    {answer.featured ? (
                      <ProductPreview
                        key={answer.id}
                        data-testid="SW-EC-CarouselItem"
                        schema={schema}
                        quantity={quantity}
                        findability={findability}
                        answer={answer}
                        index={index}
                        disabled={'focus' == schema.mode_type && !answer.focused}
                        showButtons={false}
                        fit={viewport.mobile ? 2 : 3}
                        showSponsoredLabel={false}
                        onItemSelect={index => {
                          onModalClose()
                          onItemClose()
                          onItemSelect(index)
                        }}
                        onAddToCart={onAddToCart}
                      />
                    ) : null}
                  </>
                ))}
              </MultipleCarousel>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AddedToCartModal
